/**
 * User-level hall-plan preferences: which taxonomy hovering highlights, and
 * where the block/row/seat detail-level switches sit. Unlike viewState.ts
 * (per event session) these apply across every hall plan the user opens, so
 * they're stored under one global key — the same pattern lib/theme.tsx uses
 * for the theme preference.
 */
export type HoverHighlightMode = "bezirk" | "region";

export interface HallPlanPreferences {
  hoverHighlightMode: HoverHighlightMode;
  /** Zoom percent below which the block overview is shown. */
  lodBlockMaxPercent: number;
  /** Zoom percent below which row clusters are shown (at/above it, individual seats). */
  lodRowMaxPercent: number;
}

const STORAGE_KEY = "seatingplan.hallplan.preferences";

export const DEFAULT_HALLPLAN_PREFERENCES: HallPlanPreferences = {
  hoverHighlightMode: "bezirk",
  lodBlockMaxPercent: 150,
  lodRowMaxPercent: 300,
};

export function loadHallPlanPreferences(): HallPlanPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_HALLPLAN_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      hoverHighlightMode: parsed?.hoverHighlightMode === "region" ? "region" : "bezirk",
      lodBlockMaxPercent: Number.isFinite(parsed?.lodBlockMaxPercent) ? parsed.lodBlockMaxPercent : DEFAULT_HALLPLAN_PREFERENCES.lodBlockMaxPercent,
      lodRowMaxPercent: Number.isFinite(parsed?.lodRowMaxPercent) ? parsed.lodRowMaxPercent : DEFAULT_HALLPLAN_PREFERENCES.lodRowMaxPercent,
    };
  } catch {
    // Private browsing / corrupted value — fall back to defaults.
    return DEFAULT_HALLPLAN_PREFERENCES;
  }
}

export function saveHallPlanPreferences(prefs: HallPlanPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota exceeded or storage disabled — losing the preference isn't worth surfacing an error for.
  }
}
